import React, { useEffect, useMemo, useState } from "react";
import { X, Save } from "lucide-react";
import "../../Product/Scheduling/scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import Toast from "./Toast";

const DOW = [
  { k: 0, label: "Sun" },
  { k: 1, label: "Mon" },
  { k: 2, label: "Tue" },
  { k: 3, label: "Wed" },
  { k: 4, label: "Thu" },
  { k: 5, label: "Fri" },
  { k: 6, label: "Sat" },
];

function normalizeRows(rows) {
  const map = new Map((rows || []).map((r) => [r.day_of_week, r]));
  return DOW.map(({ k }) => {
    const r = map.get(k);
    return {
      day_of_week: k,
      is_closed: r ? !!r.is_closed : false,
      start_time_local: r?.start_time_local || "09:00",
      end_time_local: r?.end_time_local || "17:00",
    };
  });
}

export default function BusinessHoursModal({ open, onClose, space }) {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const title = useMemo(() => {
    if (!space) return "Business Hours";
    return `Business Hours — ${space.name}`;
  }, [space]);

  const [rows, setRows] = useState(() => normalizeRows([]));

  useEffect(() => {
    if (!open || !companyId || !space?.id) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data, error } = await supabase.rpc(
          "rpc_get_space_business_hours",
          {
            p_company_id: companyId,
            p_space_id: space.id,
          }
        );
        if (error) throw error;
        setRows(normalizeRows(data || []));
      } catch (e) {
        setErr(e?.message || "Failed to load business hours");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, companyId, space?.id]);

  if (!open) return null;

  const setRow = (dow, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.day_of_week === dow ? { ...r, ...patch } : r))
    );
  };

  const validate = () => {
    if (!companyId) return "Company not loaded";
    if (!space?.id) return "Space not selected";
    if (!Array.isArray(rows) || rows.length !== 7) return "Invalid rows";
    for (const r of rows) {
      if (r.is_closed) continue;
      if (!r.start_time_local || !r.end_time_local)
        return "Start/end time is required for open days";
      if (r.end_time_local <= r.start_time_local)
        return "End time must be after start time";
    }
    return "";
  };

  async function save() {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setBusy(true);
    setErr("");
    try {
      const payload = rows.map((r) => ({
        day_of_week: r.day_of_week,
        is_closed: !!r.is_closed,
        start_time_local: r.is_closed ? null : r.start_time_local,
        end_time_local: r.is_closed ? null : r.end_time_local,
      }));

      const { error } = await supabase.rpc("rpc_set_space_business_hours", {
        p_company_id: companyId,
        p_space_id: space.id,
        p_rows: payload,
      });

      if (error) throw error;

      setToast({
        open: true,
        type: "success",
        message: "Business hours saved",
      });
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to save business hours");
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Failed to save business hours",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sched-modal-backdrop" onMouseDown={onClose} />
      <div
        className="sched-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Business hours"
      >
        <div className="sched-modal-head">
          <div>
            <h3 className="sched-h3">{title}</h3>
            <p className="sched-muted">
              Used only when the space has <b>Business Hours enabled</b>. If a
              day has no row, booking is rejected (strict).
            </p>
          </div>
          <button
            className="sched-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="sched-form">
          {loading ? (
            <div className="sched-muted">Loading…</div>
          ) : (
            <div className="sched-bh-table">
              <div className="sched-bh-head">
                <span>Day</span>
                <span>Closed</span>
                <span>Start</span>
                <span>End</span>
              </div>

              {rows.map((r) => (
                <div className="sched-bh-row" key={r.day_of_week}>
                  <span className="sched-bh-day">
                    {DOW.find((d) => d.k === r.day_of_week)?.label}
                  </span>

                  <label className="sched-check">
                    <input
                      type="checkbox"
                      checked={!!r.is_closed}
                      onChange={(e) =>
                        setRow(r.day_of_week, { is_closed: e.target.checked })
                      }
                      disabled={busy}
                    />
                    <span />
                  </label>

                  <input
                    type="time"
                    value={r.start_time_local}
                    onChange={(e) =>
                      setRow(r.day_of_week, {
                        start_time_local: e.target.value,
                      })
                    }
                    disabled={busy || r.is_closed}
                  />

                  <input
                    type="time"
                    value={r.end_time_local}
                    onChange={(e) =>
                      setRow(r.day_of_week, { end_time_local: e.target.value })
                    }
                    disabled={busy || r.is_closed}
                  />
                </div>
              ))}
            </div>
          )}

          {err ? <div className="sched-error">{err}</div> : null}

          <div className="sched-actions">
            <button
              className="sched-btn ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              className="sched-btn primary"
              onClick={save}
              disabled={busy || loading}
            >
              <Save size={16} /> {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </>
  );
}
